import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion'
import { usePresence } from '../../hooks/usePresence'
import { menuSize, placeAnchoredMenu } from '../../utils/menuPlacement'

export default function ScanGlassSelect({
  id,
  label,
  value,
  options = [],
  onChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 220 })
  const [activeIndex, setActiveIndex] = useState(0)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const frostRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(open, { duration: 160, reduced: reducedMotion })
  const listId = useId()
  const selected = options.find((option) => option.value === value) || options[0]
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value))

  useLayoutEffect(() => {
    if (!mounted) return undefined
    const update = () => {
      const trigger = triggerRef.current?.getBoundingClientRect()
      if (!trigger) return
      const size = menuSize(menuRef.current)
      const width = Math.max(trigger.width, size.width, 180)
      const placed = placeAnchoredMenu({
        trigger,
        width,
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        align: 'start',
        prefer: 'below',
      })
      setCoords((prev) => (
        prev.left === placed.left && prev.top === placed.top && prev.width === width
          ? prev
          : { ...placed, width }
      ))
      if (frostRef.current) {
        void getComputedStyle(frostRef.current).backdropFilter
      }
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [mounted, options, value])

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex)
  }, [open, selectedIndex])

  useEffect(() => {
    if (!mounted) return undefined
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [mounted])

  const move = (delta) => {
    if (!options.length) return
    setActiveIndex((index) => (index + delta + options.length) % options.length)
  }

  const choose = (next) => {
    onChange?.(next)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const activeOptionId = mounted ? `${listId}-opt-${activeIndex}` : undefined

  return (
    <div className="scan-glass-select">
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={`scan-glass-select-trigger${open ? ' is-open' : ''}`}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? activeOptionId : undefined}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            if (!open) setOpen(true)
            else move(1)
            return
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            if (!open) setOpen(true)
            else move(-1)
            return
          }
          if (event.key === 'Home' && open) {
            event.preventDefault()
            setActiveIndex(0)
            return
          }
          if (event.key === 'End' && open) {
            event.preventDefault()
            setActiveIndex(options.length - 1)
            return
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!open) {
              setOpen(true)
              return
            }
            choose(options[activeIndex]?.value)
            return
          }
          if (event.key === 'Escape' && open) {
            event.preventDefault()
            event.stopPropagation()
            setOpen(false)
          }
        }}
      >
        <span className="scan-glass-select-value">{selected?.label || label}</span>
        <span className="scan-glass-select-caret" aria-hidden="true" />
      </button>
      {mounted && typeof document !== 'undefined' ? createPortal(
        <div className="scan-glass-select-root" style={{ left: coords.left, top: coords.top, width: coords.width }}>
          <div
            ref={menuRef}
            className={`scan-glass-select-menu lg-panel${leaving ? ' is-leaving' : ''}`}
            role="listbox"
            id={listId}
            aria-label={label}
          >
            <div className="lg-panel-frost" ref={frostRef} aria-hidden="true" />
            <div className="lg-panel-tint" aria-hidden="true" />
            <div className="lg-panel-shine" aria-hidden="true" />
            <div className={`lg-panel-body scan-glass-select-motion${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}>
              {options.map((option, index) => {
                const isSelected = option.value === value
                return (
                  <div
                    key={String(option.value)}
                    id={`${listId}-opt-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    className={`scan-glass-select-option${isSelected ? ' is-selected' : ''}${index === activeIndex ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      choose(option.value)
                    }}
                  >
                    <span>{option.label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}
